import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { watchlistAPI } from '../services/api';
import { HiOutlineFilm, HiOutlineStar, HiOutlineCalendar } from 'react-icons/hi';
import toast from 'react-hot-toast';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w342';

const Top10 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [monthData, setMonthData] = useState({ month: 0, year: 0 });
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [activeTab, setActiveTab] = useState('movies');

  useEffect(() => {
    fetchTop10();
  }, []);

  const fetchTop10 = async () => {
    try {
      setLoading(true);
      // Add timestamp to prevent caching
      const response = await watchlistAPI.getTop10Monthly();
      const data = response.data;
      setMonthData({ month: data.month, year: data.year });
      setMovies(data.movies);
      setSeries(data.series);
    } catch (error) {
      console.error('Error fetching top 10:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Formata o mês/ano baseado nas traduções
  const formatMonthYear = () => {
    const monthNames = [
      t('settings.monthJan'),
      t('settings.monthFeb'),
      t('settings.monthMar'),
      t('settings.monthApr'),
      t('settings.monthMay'),
      t('settings.monthJun'),
      t('settings.monthJul'),
      t('settings.monthAug'),
      t('settings.monthSep'),
      t('settings.monthOct'),
      t('settings.monthNov'),
      t('settings.monthDec')
    ];
    if (monthData.month === 0) return '';
    return `${monthNames[monthData.month - 1]}/${monthData.year}`;
  };

  const monthYear = formatMonthYear();

  const getTrophyIcon = (rank) => {
    if (rank <= 3) return rank;
    return null;
  };

  const getPosterUrl = (path) => {
    if (!path) return '/default-poster.png';
    return `${TMDB_IMAGE_BASE}${path}`;
  };

  const renderTop10Row = (items) => {
    if (items.length === 0) {
      return (
        <div className="top10-row-empty">
          <HiOutlineFilm size={32} />
          <p>{t('top10.noData')}</p>
        </div>
      );
    }

    const firstRow = items.slice(0, 5);
    const secondRow = items.slice(5, 10);

    return (
      <>
        <div className="top10-row">
          {firstRow.map((item) => {
          const trophy = getTrophyIcon(item.rank);
          return (
            <div key={item.tmdb_id} className={`top10-card-large rank-${item.rank}`}>
              <div className="top10-card-poster">
                <img
                  src={getPosterUrl(item.poster_path)}
                  alt={item.title}
                  loading="lazy"
                  onError={(e) => { e.target.src = '/default-poster.png'; }}
                />
                <div className="top10-card-rank">
                  {trophy ? (
                    <span className="top10-card-trophy">{trophy}</span>
                  ) : (
                    <span className="top10-card-number">#{item.rank}</span>
                  )}
                </div>
                {item.rank <= 3 && <div className={`top10-card-glow rank-${item.rank}`} />}
              </div>
              <div className="top10-card-info">
                <h3 className="top10-card-title">{item.title}</h3>
                <div className="top10-card-meta">
                  {item.vote_average > 0 && (
                    <span className="top10-card-rating">
                      <HiOutlineStar size={12} />
                      {item.vote_average.toFixed(1)}
                    </span>
                  )}
                  <span className="top10-card-count">
                    {item.count} {item.count === 1 ? t('top10.added') : t('top10.addedPlural')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        </div>
        {secondRow.length > 0 && (
          <div className="top10-row">
            {secondRow.map((item) => {
              const trophy = getTrophyIcon(item.rank);
              return (
                <div key={item.tmdb_id} className={`top10-card-large rank-${item.rank}`}>
                  <div className="top10-card-poster">
                    <img
                      src={getPosterUrl(item.poster_path)}
                      alt={item.title}
                      loading="lazy"
                      onError={(e) => { e.target.src = '/default-poster.png'; }}
                    />
                    <div className="top10-card-rank">
                      {trophy ? (
                        <span className="top10-card-trophy">{trophy}</span>
                      ) : (
                        <span className="top10-card-number">#{item.rank}</span>
                      )}
                    </div>
                    {item.rank <= 3 && <div className={`top10-card-glow rank-${item.rank}`} />}
                  </div>
                  <div className="top10-card-info">
                    <h3 className="top10-card-title">{item.title}</h3>
                    <div className="top10-card-meta">
                      {item.vote_average > 0 && (
                        <span className="top10-card-rating">
                          <HiOutlineStar size={12} />
                          {item.vote_average.toFixed(1)}
                        </span>
                      )}
                      <span className="top10-card-count">
                        {item.count} {item.count === 1 ? t('top10.added') : t('top10.addedPlural')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const currentItems = activeTab === 'movies' ? movies : series;

  // Labels traduzidos
  const moviesLabel = t('navigation.movies');
  const seriesLabel = t('navigation.series');
  const top10Title = t('top10.title');

  return (
    <div className="top10-container">
      {/* Header Fixo */}
      <div className="top10-header-sticky">
        <div className="top10-compact-header">
          <div className="top10-compact-left">
            <h1>{top10Title}</h1>
            <span className="top10-compact-info">
              <HiOutlineCalendar size={13} />
              {monthYear} • {currentItems.length} {activeTab === 'movies' ? moviesLabel : seriesLabel}
            </span>
          </div>
          <div className="top10-compact-right">
            <button
              className={`top10-tab-btn ${activeTab === 'movies' ? 'active' : ''}`}
              onClick={() => setActiveTab('movies')}
            >
              <HiOutlineFilm size={16} />
              <span>{moviesLabel}</span>
              <span className="top10-tab-count">{movies.length}</span>
            </button>
            <button
              className={`top10-tab-btn ${activeTab === 'series' ? 'active' : ''}`}
              onClick={() => setActiveTab('series')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="15" rx="2" ry="2"/>
                <polyline points="17 2 12 7 7 2"/>
              </svg>
              <span>{seriesLabel}</span>
              <span className="top10-tab-count">{series.length}</span>
            </button>
            <span className="top10-month-badge">{monthYear}</span>
          </div>
        </div>
      </div>

      {/* Content Rolável */}
      <div className="top10-content-scrollable">
        {loading ? (
          <div className="top10-loading">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <div className="top10-section-large">
            <h2 className="top10-section-header">
              {activeTab === 'movies' ? (
                <><HiOutlineFilm size={22} />{moviesLabel}</>
              ) : (
                <><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="15" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/></svg>{seriesLabel}</>
              )}
            </h2>
            {renderTop10Row(currentItems)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Top10;
